import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Shield,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Plus,
  Lock,
  Mail,
  Clock,
  X as XIcon,
  RotateCcw,
  Search,
  ChevronDown,
  Send,
  LayoutGrid,
  ClipboardList,
  Users,
  Code,
  PenTool,
  Calculator,
  Shuffle,
  ListChecks,
  Eye,
  PlayCircle,
  Video,
  Info,
  Trash2,
  Pencil,
  BadgeCheck,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import { RecruiterLoadingScreen, RecruiterPageShell } from '../../components/Recruiter/RecruiterStates';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  acceptDpa,
  cancelInvite,
  cleanTemplateTitle,
  sanitizeInterviewCopy,
  createInvite,
  createTemplate,
  deleteTemplate,
  draftTemplateFromJd,
  extractJobText,
  generateTemplatePool,
  getPoolStatus,
  type PoolGenStatus,
  fetchCurrentDpa,
  fetchDpaStatus,
  fetchPersonas,
  listInvites,
  listResults,
  listRecruiterDemos,
  startRecruiterDemo,
  getTemplate,
  listTemplates,
  updateTemplate,
  resendInvite,
  withdrawDpa,
  type DemoHistoryItem,
  type DemoStartPayload,
  type DpaCurrentResponse,
  type DpaStatusResponse,
  type InterviewTemplate,
  type InviteCreatePayload,
  type InviteCreateResponse,
  type InviteSession,
  type Persona,
  type PersonasResponse,
  type ResultSummary,
  type TemplateCreatePayload,
} from '../../api/interview';
import DpaAcceptModal from '../../components/Interview/DpaAcceptModal';
import InviteCreateModal, {
  type AtsCandidateOption,
} from '../../components/Interview/InviteCreateModal';
import TemplateBuilderModal from '../../components/Interview/TemplateBuilderModal';
import TemplateDetailModal from '../../components/Interview/TemplateDetailModal';
import PersonaVoicePlayButton from '../../components/Interview/PersonaVoicePlayButton';
import { pickSampleTaskSteps } from '../../lib/interviewPoolTasks';
import { RECRUITER_DEMO_URL } from '../../constants/recruiterContact';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Admin-gated landing page for the AI Interview feature (phase 1 dev).
 *
 * Visible only via /admin/interviews (wrapped in <AdminGuard> in App.tsx).
 * Shows:
 *   - DPA gate (must be accepted before anything else works)
 *   - Persona library preview
 *   - Placeholder cards for upcoming work (Templates, Sessions, Scorecards)
 */
const InterviewHub: React.FC = () => {
  const { apiCall } = useAuth();
  const { userProfile } = useUserProfile();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';

  // Pilot gate: during the early launch only the Star tier gets full
  // hub access. Star is not yet sold via Stripe -- we set it manually
  // on the profile for each onboarded pilot company. Platform admins
  // also pass through so the team can keep testing without flipping
  // their own tier. Once Star opens up to self-serve, this same gate
  // automatically lets paying customers through.
  const isPilot =
    userProfile?.subscription_type === 'recruiter_star' ||
    userProfile?.is_admin === true;

  const [loading, setLoading] = useState(true);
  const [headerInfoOpen, setHeaderInfoOpen] = useState(false);
  const headerInfoRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const [dpaStatus, setDpaStatus] = useState<DpaStatusResponse | null>(null);
  const [dpaCurrent, setDpaCurrent] = useState<DpaCurrentResponse | null>(null);
  const [personas, setPersonas] = useState<PersonasResponse | null>(null);
  const [templates, setTemplates] = useState<InterviewTemplate[]>([]);
  const [invites, setInvites] = useState<InviteSession[]>([]);
  const [results, setResults] = useState<ResultSummary[]>([]);
  const [demos, setDemos] = useState<DemoHistoryItem[]>([]);
  // template_id currently being launched as a demo (spinner + guard
  // against double-clicks while we mint the session + redirect).
  const [demoStarting, setDemoStarting] = useState<string | null>(null);
  // Per-template async pool-generation status (Phase 2 / Agent 2).
  const [poolGen, setPoolGen] = useState<Record<string, PoolGenStatus>>({});

  // ATS context for the invite modal: companyId is resolved once via
  // /companies/my-company (recruiter has at most one company today),
  // connections + jobs are pulled best-effort. All three are optional --
  // the modal degrades to its original behaviour if ATS is empty.
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [atsConnections, setAtsConnections] = useState<
    Array<{ provider: string; is_active: boolean; auto_push?: boolean }>
  >([]);
  const [atsJobs, setAtsJobs] = useState<
    Array<{
      ats_job_id: string;
      title: string;
      department?: string | null;
      location?: string | null;
    }>
  >([]);

  const [showDpaModal, setShowDpaModal] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  // When the recruiter clicks "Customize" inside the detail modal we
  // hand the source template to the builder so it can pre-fill its
  // fields. `null` means "blank new template".
  const [builderInitial, setBuilderInitial] = useState<InterviewTemplate | null>(null);
  const [builderEdit, setBuilderEdit] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  // When the recruiter clicks "Einladen" from a template card or its
  // detail modal we want the invite modal to open with that template
  // already selected. Null = the modal was opened generically (from
  // the "Neue Einladung" button) and should default to the first
  // template like before.
  const [invitePresetTpl, setInvitePresetTpl] = useState<InterviewTemplate | null>(null);
  const [detailTpl, setDetailTpl] = useState<InterviewTemplate | null>(null);

  // Section navigation. B2B-style horizontal tabs under the page
  // header. The active tab lives ONLY in the URL (?tab=...) so it's
  // bookmarkable / shareable AND so we never get a render where
  // local state and URL disagree -- the earlier two-source design
  // (local state + URL-sync effect) caused a one-frame flicker back
  // to the default tab while the URL update was still in flight.
  type TabId = 'mine' | 'templates' | 'invites' | 'results' | 'personas';
  const VALID_TABS: TabId[] = ['mine', 'templates', 'invites', 'results', 'personas'];
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const activeTab: TabId = VALID_TABS.includes(urlTab as TabId)
    ? (urlTab as TabId)
    : 'results';
  const goTab = useCallback(
    (next: TabId) => {
      if (next === activeTab) return;
      const params = new URLSearchParams(searchParams);
      params.set('tab', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, activeTab],
  );

  const hydrateTemplate = useCallback(
    async (templateId: string) => {
      const full = await getTemplate(apiCall, templateId);
      setTemplates((prev) =>
        prev.map((t) => (t.id === templateId ? { ...t, ...full } : t)),
      );
      setDetailTpl((prev) => (prev?.id === templateId ? full : prev));
    },
    [apiCall],
  );

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [status, current, p, tpls, inv, res, dms] = await Promise.all([
        fetchDpaStatus(apiCall).catch(() => null),
        fetchCurrentDpa(apiCall),
        fetchPersonas(apiCall),
        listTemplates(apiCall).catch(() => ({ templates: [] })),
        listInvites(apiCall, { limit: 50 }).catch(() => ({ invites: [] })),
        listResults(apiCall, { limit: 50 }).catch(() => ({ results: [] })),
        listRecruiterDemos(apiCall, 50).catch(() => ({ demos: [] })),
      ]);
      setDpaStatus(status);
      setDpaCurrent(current);
      setPersonas(p);
      setTemplates(tpls.templates);
      setInvites(inv.invites);
      setResults(res.results);
      setDemos(dms.demos);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!headerInfoOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (headerInfoRef.current && !headerInfoRef.current.contains(e.target as Node)) {
        setHeaderInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [headerInfoOpen]);

  // ATS bootstrap. Best-effort: any failure leaves the modal in its
  // non-ATS path. We don't block the page on this.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cResp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (!cResp.ok) return;
        const cData = await cResp.json();
        const cid: string | null = cData?.company?.id || null;
        if (!cid || cancelled) return;
        setCompanyId(cid);

        const [connResp, jobsResp] = await Promise.all([
          apiCall(`${API_BASE}/api/v1/ats/connections/${cid}`).catch(() => null),
          apiCall(`${API_BASE}/api/v1/ats/jobs/${cid}`).catch(() => null),
        ]);
        if (cancelled) return;
        if (connResp && connResp.ok) {
          const cdata = await connResp.json();
          setAtsConnections(Array.isArray(cdata.connections) ? cdata.connections : []);
        }
        if (jobsResp && jobsResp.ok) {
          const jdata = await jobsResp.json();
          setAtsJobs(Array.isArray(jdata.jobs) ? jdata.jobs : []);
        }
      } catch {
        /* ignore -- ATS panel is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiCall]);

  const loadAtsCandidates = useCallback(
    async (params: {
      provider: string;
      ats_job_id?: string | null;
      limit?: number;
    }): Promise<AtsCandidateOption[]> => {
      if (!companyId) return [];
      const qs = new URLSearchParams();
      qs.set('provider', params.provider);
      if (params.ats_job_id) qs.set('ats_job_id', params.ats_job_id);
      qs.set('limit', String(params.limit ?? 50));
      try {
        const r = await apiCall(
          `${API_BASE}/api/v1/ats/candidates/${companyId}?${qs.toString()}`,
        );
        if (!r.ok) return [];
        const data = await r.json();
        return Array.isArray(data.candidates) ? data.candidates : [];
      } catch {
        return [];
      }
    },
    [apiCall, companyId],
  );

  const handleAccept = async (version: string) => {
    const fresh = await acceptDpa(apiCall, version);
    setDpaStatus(fresh);
    setShowDpaModal(false);
    // Re-pull templates / invites / results so anything that was
    // hidden behind the DPA gate (e.g. invite buttons, template
    // "Einladen" actions) flips on without a manual refresh.
    loadAll();
  };

  const handleWithdraw = async () => {
    const confirmMsg = lang === 'de'
      ? 'AVV wirklich widerrufen? AI-Interviews werden sofort deaktiviert.'
      : 'Really withdraw the DPA? AI interviews will be disabled immediately.';
    if (!window.confirm(confirmMsg)) return;
    const fresh = await withdrawDpa(apiCall);
    setDpaStatus(fresh);
  };

  const handleCreateTemplate = async (payload: TemplateCreatePayload) => {
    const tpl = await createTemplate(apiCall, payload);
    setTemplates((prev) => [tpl, ...prev]);
    return tpl;
  };

  const handleUpdateTemplate = async (templateId: string, payload: TemplateCreatePayload) => {
    const tpl = await updateTemplate(apiCall, templateId, payload);
    setTemplates((prev) => prev.map((x) => (x.id === templateId ? { ...x, ...tpl } : x)));
    const fresh = await listTemplates(apiCall).catch(() => null);
    if (fresh) setTemplates(fresh.templates);
    return tpl;
  };

  const handleEditTemplate = (tpl: InterviewTemplate) => {
    setBuilderInitial(tpl);
    setBuilderEdit(true);
    setShowBuilder(true);
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);
  // A family can have DE + EN sibling rows -- delete all of them so the card
  // disappears completely rather than leaving the other language behind.
  const handleDeleteTemplate = async (templateIds: string[]) => {
    const ids = templateIds.filter(Boolean);
    if (ids.length === 0) return;
    setDeletingId(ids[0]);
    try {
      for (const id of ids) {
        await deleteTemplate(apiCall, id);
      }
      setTemplates((prev) => prev.filter((tpl) => !ids.includes(tpl.id)));
      const fresh = await listTemplates(apiCall).catch(() => null);
      if (fresh) setTemplates(fresh.templates);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDraftFromJd = (jobDescription: string, language?: 'de' | 'en') =>
    draftTemplateFromJd(apiCall, { jobDescription, language });

  const handleExtractJobText = (file: File) => extractJobText(apiCall, file);

  // Templates currently being polled, so a re-mount / refresh doesn't spin up
  // duplicate poll loops for the same template.
  const poolPollingRef = useRef<Set<string>>(new Set());

  const pollPoolStatus = useCallback((templateId: string) => {
    if (poolPollingRef.current.has(templateId)) return;
    poolPollingRef.current.add(templateId);
    const started = Date.now();
    const poll = async () => {
      try {
        const s = await getPoolStatus(apiCall, templateId);
        setPoolGen((m) => ({ ...m, [templateId]: s }));
        if (s.status === 'done') {
          poolPollingRef.current.delete(templateId);
          const fresh = await listTemplates(apiCall).catch(() => null);
          if (fresh) setTemplates(fresh.templates);
          return;
        }
        if (s.status === 'failed') {
          poolPollingRef.current.delete(templateId);
          return;
        }
      } catch {
        // transient -- keep polling within the window
      }
      // Large pools (batched, up to 100 cases) can run for several minutes.
      if (Date.now() - started < 15 * 60 * 1000) {
        setTimeout(poll, 2500);
      } else {
        poolPollingRef.current.delete(templateId);
      }
    };
    setTimeout(poll, 2500);
  }, [apiCall]);

  const handleGeneratePool = async (templateId: string, nCases: number, append = false) => {
    setPoolGen((m) => ({ ...m, [templateId]: { status: 'queued' } }));
    try {
      await generateTemplatePool(apiCall, templateId, nCases, append);
    } catch (e) {
      setPoolGen((m) => ({
        ...m,
        [templateId]: { status: 'failed', error: e instanceof Error ? e.message : String(e) },
      }));
      return;
    }
    pollPoolStatus(templateId);
  };

  // Called by each eligible card on mount: if a generation is already running
  // (e.g. the recruiter refreshed or came back later), resume showing progress.
  const handleCheckPoolStatus = useCallback(async (templateId: string) => {
    try {
      const s = await getPoolStatus(apiCall, templateId);
      if (s.status === 'queued' || s.status === 'running') {
        setPoolGen((m) => ({ ...m, [templateId]: s }));
        pollPoolStatus(templateId);
      }
    } catch {
      // ignore -- best effort
    }
  }, [apiCall, pollPoolStatus]);

  const handleCreateInvite = async (payload: InviteCreatePayload): Promise<InviteCreateResponse> => {
    const resp = await createInvite(apiCall, payload);
    setInvites((prev) => [resp.session, ...prev]);
    return resp;
  };

  // Recruiter demo: spin up a self-run of the template and drop the
  // recruiter straight into the candidate landing/lobby for that token.
  // Same entry path as a consumer mock (/interview/invite/:token) so the
  // experience is identical to what candidates get.
  const handleStartDemo = async (payload: DemoStartPayload) => {
    if (demoStarting) return;
    setDemoStarting(payload.template_id);
    try {
      const res = await startRecruiterDemo(apiCall, payload);
      const token = res.raw_token || res.invite_url.split('/').pop();
      navigate(`/interview/invite/${token}`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
      setDemoStarting(null);
    }
  };

  const handleCancelInvite = async (sessionId: string) => {
    const ok = window.confirm(
      lang === 'de'
        ? 'Einladung wirklich abbrechen?'
        : 'Really cancel this invite?',
    );
    if (!ok) return;
    try {
      await cancelInvite(apiCall, sessionId);
      setInvites((prev) =>
        prev.map((i) => (i.id === sessionId ? { ...i, status: 'cancelled' } : i)),
      );
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  const handleResendInvite = async (sessionId: string) => {
    try {
      const resp = await resendInvite(apiCall, sessionId);
      setInvites((prev) =>
        prev.map((i) => (i.id === sessionId ? resp.session : i)),
      );
      window.prompt(
        lang === 'de' ? 'Neuer Magic-Link (kopierbar):' : 'New magic link (copy):',
        resp.invite_url,
      );
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  const hasActiveDpa = !!dpaStatus?.has_active_dpa;

  const t = useMemo(
    () => ({
      pageTitle: lang === 'de' ? 'AI Interview Plattform' : 'AI Interview Platform',
      headerTitle: lang === 'de' ? 'KI-Interviews' : 'AI Interviews',
      pageSubtitle: lang === 'de'
        ? 'Automatisierte Video-Interviews mit KI. DSGVO-konform, Hosting in der EU.'
        : 'Automated AI-led video interviews. GDPR-compliant, EU-hosted.',
      headerInfoLabel: lang === 'de' ? 'Info zu KI-Interviews' : 'About AI interviews',
      headerInfoBullets: lang === 'de'
        ? [
            'Strukturierte Scorecard, Transkript und vollständiges Video-Replay pro abgeschlossenem Interview.',
            'EU-Hosting (Azure France Central). Keine Trainingsnutzung deiner Interviewdaten.',
            'KI-Scorecard als Empfehlung; die finale Entscheidung trifft immer ein Mensch (Art. 22 DSGVO).',
            'AVV mit deinem Unternehmen nötig, bevor Einladungen und eigene Templates möglich sind.',
          ]
        : [
            'Structured scorecard, transcript and full video replay for every completed interview.',
            'EU hosting (Azure France Central). Your interview data is not used for model training.',
            'AI scorecard is a recommendation only; a human always makes the final decision (GDPR Art. 22).',
            'A DPA with your company is required before you can send invites or create custom templates.',
          ],
      dpaTitleMissing: lang === 'de' ? 'AVV ausstehend' : 'DPA pending',
      dpaTitleActive:  lang === 'de' ? 'AVV aktiv'     : 'DPA active',
      dpaBodyMissing:  lang === 'de'
        ? 'Akzeptiere den AVV einmalig pro Unternehmen, um AI-Interviews zu aktivieren.'
        : 'Accept the DPA once per company to enable AI interviews.',
      dpaBodyActive:   lang === 'de' ? 'Aktive Version'    : 'Active version',
      acceptedAt:      lang === 'de' ? 'Akzeptiert am'    : 'Accepted on',
      acceptBtn:       lang === 'de' ? 'AVV akzeptieren'  : 'Accept DPA',
      withdrawBtn:     lang === 'de' ? 'AVV widerrufen'   : 'Withdraw DPA',
      personasTitle:   lang === 'de' ? 'AI-Interviewer Personas' : 'AI Interviewer Personas',
      personasSubtitle:lang === 'de'
        ? 'Eine wählbare AI-Persona pro Template, konfigurierbar im Builder'
        : 'Pick an AI persona per template, configurable in the builder',
      tplTitle:        lang === 'de' ? 'Interview-Templates' : 'Interview Templates',
      tplSubtitle:     lang === 'de'
        ? 'Fragenpools für Einladungen. Level und Dauer (30 / 45 / 60 Min.) legst du pro Kandidat:in in der Einladung fest.'
        : 'Question pools for invites. Set level and duration (30 / 45 / 60 min) per candidate in the invite.',
      tplPublic:       lang === 'de' ? 'Cevee Standard' : 'Cevee curated',
      tplPrivate:      lang === 'de' ? 'Privat' : 'Private',
      tplCreate:       lang === 'de' ? 'Neues Interview' : 'New interview',
      tplGated:        lang === 'de'
        ? 'AVV muss akzeptiert sein, um eigene Templates zu erstellen'
        : 'Accept the DPA to create your own templates',
      tplEmpty:        lang === 'de' ? 'Noch keine Templates' : 'No templates yet',
      tplMin:          lang === 'de' ? 'Min' : 'Min',
      tplInvite:       lang === 'de' ? 'Einladen' : 'Invite',
      tplDemo:         lang === 'de' ? 'Demo' : 'Demo',
      tplDemoHint:     lang === 'de'
        ? 'Demo starten: gleicher Fragenpool und Ablauf wie bei Kandidat:innen (Fragen variieren leicht) -- ohne Quota.'
        : 'Start a demo: same question pool and flow as for candidates (questions may vary slightly) -- no quota.',
      tplSearch:       lang === 'de' ? 'Templates durchsuchen ...' : 'Search templates ...',
      tplNoMatch:      lang === 'de' ? 'Keine Treffer für diese Filter.' : 'No matches for these filters.',
      tplAll:          lang === 'de' ? 'Alle' : 'All',
      tplArea:         lang === 'de' ? 'Bereich' : 'Area',
      tplLevel:        lang === 'de' ? 'Level' : 'Level',
      tplTypeFilter:   lang === 'de' ? 'Typ' : 'Type',
      lvlStudent:      lang === 'de' ? 'Studi' : 'Student',
      areaRecommended: lang === 'de' ? 'Empfohlen' : 'Recommended',
      areaTech:        'Tech',
      areaProduct:     'Product',
      areaSales:       'Sales / GTM',
      areaOperations:  lang === 'de' ? 'Operations / Logistik' : 'Operations / Logistics',
      areaTrades:      lang === 'de' ? 'Handwerk / Bau' : 'Skilled trades',
      areaService:     lang === 'de' ? 'Kundenservice' : 'Customer service',
      areaConsulting:  'Consulting',
      areaMarketing:   'Marketing',
      areaFinance:     'Finance',
      areaAcademic:    lang === 'de' ? 'Forschung' : 'Academic',
      areaGeneral:     lang === 'de' ? 'Allgemein / Sonstiges' : 'General / Other',
      refresh:         lang === 'de' ? 'Aktualisieren' : 'Refresh',
      errorTitle:      lang === 'de' ? 'Fehler beim Laden' : 'Loading failed',
      invitesTitle:    lang === 'de' ? 'Einladungen' : 'Invites',
      invitesSubtitle: lang === 'de'
        ? 'Versendete Magic-Links + Status der Kandidat:innen'
        : 'Magic links sent and candidate progress',
      invitesCreate:   lang === 'de' ? 'Neue Einladung' : 'New invite',
      invitesEmpty:    lang === 'de' ? 'Noch keine Einladungen versendet' : 'No invites sent yet',
      invitesGated:    lang === 'de'
        ? 'AVV muss akzeptiert sein, um Einladungen zu versenden'
        : 'Accept the DPA to send invites',
      cancelBtn:       lang === 'de' ? 'Abbrechen' : 'Cancel',
      resendBtn:       lang === 'de' ? 'Neu senden' : 'Resend',
      sent:            lang === 'de' ? 'Versendet' : 'Sent',
      expiresAt:       lang === 'de' ? 'Gültig bis' : 'Valid until',
    }),
    [lang],
  );

  // Cevee = curated public library; "mine" = this company's own templates
  // (incl. AI-drafted ones and recruiter-generated pools). Splitting them at
  // the tab level keeps the landing ("Cevee-Vorlagen") lean.
  const ceveeTemplates = useMemo(() => templates.filter((t) => t.is_public), [templates]);
  const ownTemplates = useMemo(() => templates.filter((t) => !t.is_public), [templates]);

  const tabCounts = {
    templates: ceveeTemplates.length,
    mine: ownTemplates.length,
    invites: invites.length,
    results: results.length,
    personas: personas?.personas.length || 0,
  };

  if (!isPilot) {
    const demoCopy = lang === 'de'
      ? {
          eyebrow: 'KI-Vorstellungsgespräche · Closed Pilot',
          title: 'KI-Interviews für dein Recruiting',
          subtitle:
            'Lass Kandidat:innen asynchron in einer realistischen, KI-geführten Vorstellungsgespräch-Session zeigen, was sie können - mit strukturierter Scorecard, Transkript und Video-Replay.',
          bullets: [
            'Eigene Interviews gestalten - per KI aus einer Stellenbeschreibung in Minuten',
            '24/7 asynchron: Kandidat:innen starten jederzeit ein KI-geführtes Video-Interview',
            'Strukturierte Scorecard, Transkript & Video-Replay - finale Entscheidung trifft ein Mensch (Art. 22 DSGVO)',
            'EU-gehostet (Azure France Central), AVV inklusive · optionaler ATS-Push (Ashby)',
          ],
          tierLine:
            'Aktuell laufen wir ein eingeladenes Pilot-Programm mit ausgewählten Recruiting-Teams.',
          ctaBook: 'Demo buchen',
          ctaMail: 'oder schreib direkt an Lea: lea.anders@cevee.eu',
          tierBadge: 'Dein Plan: ',
          tierFree: 'Free',
          tierPremium: 'Premium',
          tierOther: 'Eingeloggt',
        }
      : {
          eyebrow: 'AI Interviews · Closed Pilot',
          title: 'AI-led interviews for your recruiting',
          subtitle:
            'Let candidates show what they can do in an async, AI-led interview session - with a structured scorecard, transcript and video replay.',
          bullets: [
            'Design your own interviews - AI-drafted from a job description in minutes',
            '24/7 async: candidates start an AI-led video interview whenever they want',
            'Structured scorecard, transcript & video replay - final decision always made by a human (Art. 22 GDPR)',
            'EU-hosted (Azure France Central), DPA included · optional ATS push (Ashby)',
          ],
          tierLine:
            'We\u2019re currently running an invite-only pilot with selected recruiting teams.',
          ctaBook: 'Book a demo',
          ctaMail: 'or email Lea directly: lea.anders@cevee.eu',
          tierBadge: 'Your plan: ',
          tierFree: 'Free',
          tierPremium: 'Premium',
          tierOther: 'Signed in',
        };

    const sub = userProfile?.subscription_type || '';
    const tierLabel = sub.endsWith('premium')
      ? demoCopy.tierPremium
      : sub.endsWith('free')
        ? demoCopy.tierFree
        : demoCopy.tierOther;

    return (
      <RecruiterPageShell contentClassName="max-w-3xl py-16 sm:py-20">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-pink-50 text-pink-600 border border-pink-100">
                {demoCopy.eyebrow}
              </span>
              <span className="text-[10px] font-medium text-slate-500">
                {demoCopy.tierBadge}
                <span className="font-semibold text-slate-700">{tierLabel}</span>
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-3">
              {demoCopy.title}
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6">{demoCopy.subtitle}</p>

            <ul className="space-y-2 mb-6">
              {demoCopy.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <p className="text-sm text-slate-500 mb-6">{demoCopy.tierLine}</p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <a
                href={RECRUITER_DEMO_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
              >
                <Sparkles className="w-4 h-4" />
                {demoCopy.ctaBook}
              </a>
              <a
                href="mailto:lea.anders@cevee.eu?subject=Cevee%20Interview%20Pilot"
                className="text-xs text-slate-500 transition-colors hover:text-slate-800 hover:underline"
              >
                {demoCopy.ctaMail}
              </a>
            </div>
          </div>
      </RecruiterPageShell>
    );
  }

  if (loading) {
    return <RecruiterLoadingScreen icon={Video} />;
  }

  return (
    <RecruiterPageShell>
      <div className="mb-4 flex items-center justify-end gap-3">
        <div className="relative flex-shrink-0" ref={headerInfoRef}>
          <button
            type="button"
            onClick={() => setHeaderInfoOpen((o) => !o)}
            aria-expanded={headerInfoOpen}
            aria-label={t.headerInfoLabel}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <Info className="size-4" />
            {lang === 'de' ? 'Was ist das?' : 'What is this?'}
          </button>
          {headerInfoOpen && (
            <div className="absolute right-0 top-full z-50 mt-1.5 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-3.5 shadow-lg">
              <p className="text-[13px] font-medium leading-relaxed text-slate-700">
                {t.pageSubtitle}
              </p>
              <ul className="mt-2.5 list-disc space-y-1.5 pl-4 text-[12px] leading-snug text-slate-600">
                {t.headerInfoBullets.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

        {error && !loading && (
          <div className="flex items-start gap-3 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-700">{t.errorTitle}</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {!loading && dpaCurrent && (
          <DpaCard
            status={dpaStatus}
            current={dpaCurrent}
            labels={t}
            onAccept={() => setShowDpaModal(true)}
            onWithdraw={handleWithdraw}
          />
        )}

        {!loading && (
          <SectionTabs
            active={activeTab}
            onChange={goTab}
            counts={tabCounts}
            lang={lang}
          />
        )}

        {!loading && activeTab === 'templates' && (
          <TemplateSection
            mode="cevee"
            templates={ceveeTemplates}
            hasActiveDpa={hasActiveDpa}
            onView={(tpl) => setDetailTpl(tpl)}
            onInvite={(tpl) => {
              setInvitePresetTpl(tpl);
              setShowInviteModal(true);
            }}
            onDemo={handleStartDemo}
            demoStarting={demoStarting}
            labels={t}
            lang={lang}
          />
        )}

        {!loading && activeTab === 'mine' && (
          <TemplateSection
            mode="mine"
            templates={ownTemplates}
            hasActiveDpa={hasActiveDpa}
            onCreate={() => {
              setBuilderInitial(null);
              setBuilderEdit(false);
              setShowBuilder(true);
            }}
            onView={(tpl) => setDetailTpl(tpl)}
            onInvite={(tpl) => {
              setInvitePresetTpl(tpl);
              setShowInviteModal(true);
            }}
            onDemo={handleStartDemo}
            demoStarting={demoStarting}
            poolGen={poolGen}
            onGeneratePool={hasActiveDpa ? handleGeneratePool : undefined}
            onCheckPoolStatus={handleCheckPoolStatus}
            onEdit={handleEditTemplate}
            onDelete={handleDeleteTemplate}
            deletingId={deletingId}
            labels={t}
            lang={lang}
          />
        )}

        {!loading && activeTab === 'invites' && (
          <InvitesSection
            invites={invites}
            templates={templates}
            personas={personas?.personas || []}
            hasActiveDpa={hasActiveDpa}
            labels={t}
            lang={lang}
            onCreate={() => setShowInviteModal(true)}
            onCancel={handleCancelInvite}
            onResend={handleResendInvite}
          />
        )}

        {!loading && activeTab === 'results' && (
          <ResultsSection results={results} demos={demos} lang={lang} />
        )}

        {!loading && activeTab === 'personas' && personas && (
          <PersonaGallery
            personas={personas.personas}
            defaultId={personas.default_persona_id}
            lang={lang}
            labels={t}
          />
        )}

        {showDpaModal && dpaCurrent && (
          <DpaAcceptModal
            isOpen={showDpaModal}
            dpa={dpaCurrent.current}
            onAccept={handleAccept}
            onClose={() => setShowDpaModal(false)}
          />
        )}

        {showBuilder && personas && (
          <TemplateBuilderModal
            isOpen={showBuilder}
            personas={personas.personas}
            initial={builderInitial}
            isEdit={builderEdit}
            onClose={() => {
              setShowBuilder(false);
              setBuilderInitial(null);
              setBuilderEdit(false);
            }}
            onCreate={handleCreateTemplate}
            onUpdate={handleUpdateTemplate}
            onDraftFromJd={handleDraftFromJd}
            onExtractJobText={handleExtractJobText}
          />
        )}

        {showInviteModal && personas && (
          <InviteCreateModal
            isOpen={showInviteModal}
            templates={templates}
            personas={personas.personas}
            presetTemplate={invitePresetTpl}
            onClose={() => {
              setShowInviteModal(false);
              setInvitePresetTpl(null);
            }}
            onCreate={handleCreateInvite}
            onDemo={(payload) => {
              setShowInviteModal(false);
              setInvitePresetTpl(null);
              void handleStartDemo(payload);
            }}
            demoStarting={demoStarting}
            atsConnections={atsConnections}
            atsJobs={atsJobs}
            onLoadAtsCandidates={loadAtsCandidates}
          />
        )}

        {detailTpl && personas && (
          <TemplateDetailModal
            isOpen={!!detailTpl}
            templates={templates}
            initial={detailTpl}
            personas={personas.personas}
            sessionConfigurable={!!detailTpl?.sampling_strategy}
            onNeedTemplate={(id) => { void hydrateTemplate(id); }}
            onClose={() => setDetailTpl(null)}
            canUseAsTemplate={!!dpaStatus?.has_active_dpa}
            onUseAsTemplate={(tpl) => {
              setDetailTpl(null);
              setBuilderInitial(tpl);
              setBuilderEdit(false);
              setShowBuilder(true);
            }}
            canInvite={!!dpaStatus?.has_active_dpa}
            onInvite={(tpl) => {
              setDetailTpl(null);
              setInvitePresetTpl(tpl);
              setShowInviteModal(true);
            }}
          />
        )}
    </RecruiterPageShell>
  );
};

// ============================================================
// Sub-components
// ============================================================

// ------------------------------------------------------------
// SectionTabs -- B2B-style horizontal tab nav under the page
// header. Mirrors Linear / Vercel / Stripe dashboards: underline
// indicator, count pill, sticky-feeling section divider.
// ------------------------------------------------------------
const SectionTabs: React.FC<{
  active: 'mine' | 'templates' | 'invites' | 'results' | 'personas';
  onChange: (id: 'mine' | 'templates' | 'invites' | 'results' | 'personas') => void;
  counts: { templates: number; mine: number; invites: number; results: number; personas: number };
  lang: 'de' | 'en';
}> = ({ active, onChange, counts, lang }) => {
  const items: {
    id: 'mine' | 'templates' | 'invites' | 'results' | 'personas';
    icon: React.ElementType;
    de: string;
    en: string;
    secondary?: boolean;
  }[] = [
    { id: 'results',   icon: ClipboardList, de: 'Ergebnisse',        en: 'Results' },
    { id: 'invites',   icon: Mail,          de: 'Einladungen',       en: 'Invites' },
    { id: 'mine',      icon: LayoutGrid,    de: 'Eigene Interviews', en: 'My interviews' },
    { id: 'templates', icon: BadgeCheck,    de: 'Cevee Interviews',  en: 'Cevee interviews' },
    { id: 'personas',  icon: Users,         de: 'Personas',          en: 'Personas', secondary: true },
  ];
  return (
    <div className="scrollbar-hide mb-6 flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
      {items.map((it) => {
        const isActive = it.id === active;
        const Icon = it.icon;
        const count = counts[it.id];
        return (
          <button
            key={it.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(it.id)}
            className={`flex min-w-fit flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-[13px] font-semibold transition ${
              isActive
                ? 'bg-slate-900 text-white'
                : it.secondary
                  ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                  : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{lang === 'de' ? it.de : it.en}</span>
            {count > 0 && (
              <span className={`rounded-full px-1.5 text-[10px] font-bold ${
                isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

interface Labels {
  dpaTitleMissing: string;
  dpaTitleActive: string;
  dpaBodyMissing: string;
  dpaBodyActive: string;
  acceptedAt: string;
  acceptBtn: string;
  withdrawBtn: string;
  personasTitle: string;
  personasSubtitle: string;
  tplTitle: string;
  tplSubtitle: string;
  tplPublic: string;
  tplPrivate: string;
  tplCreate: string;
  tplGated: string;
  tplEmpty: string;
  tplSearch: string;
  tplNoMatch: string;
  tplAll: string;
  tplArea: string;
  tplLevel: string;
  tplTypeFilter: string;
  lvlStudent: string;
  areaRecommended: string;
  areaTech: string;
  areaProduct: string;
  areaSales: string;
  areaOperations: string;
  areaTrades: string;
  areaService: string;
  areaConsulting: string;
  areaMarketing: string;
  areaFinance: string;
  areaAcademic: string;
  areaGeneral: string;
  tplMin: string;
  tplInvite: string;
  tplDemo: string;
  tplDemoHint: string;
  invitesTitle: string;
  invitesSubtitle: string;
  invitesCreate: string;
  invitesEmpty: string;
  invitesGated: string;
  cancelBtn: string;
  resendBtn: string;
  sent: string;
  expiresAt: string;
}

const DpaCard: React.FC<{
  status: DpaStatusResponse | null;
  current: DpaCurrentResponse;
  labels: Labels;
  onAccept: () => void;
  onWithdraw: () => void;
}> = ({ status, current, labels, onAccept, onWithdraw }) => {
  const isActive = !!status?.has_active_dpa;

  // Active state: minimal one-line status strip (the recruiter doesn't
  // need a 6-line hero telling them everything is fine). Only blow it
  // up to a full card when there's actually something to do (DPA
  // missing / outdated).
  if (isActive) {
    return (
      <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-emerald-200/60 bg-emerald-50/30 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0 text-sm text-slate-700">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="font-medium text-slate-900">{labels.dpaTitleActive}</span>
          <span className="text-slate-400">·</span>
          <span className="font-mono text-xs text-slate-500">v{status?.current_dpa_version}</span>
          {status?.dpa_accepted_at && (
            <span className="hidden sm:inline text-xs text-slate-400 truncate">
              · {formatEpoch(status.dpa_accepted_at)}
            </span>
          )}
        </div>
        <button
          onClick={onWithdraw}
          className="text-[11px] font-medium text-slate-500 hover:text-amber-700 underline-offset-2 hover:underline whitespace-nowrap"
        >
          {labels.withdrawBtn}
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border border-amber-200/70 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
          <Shield className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-slate-900 mb-1 tracking-tight">
            {labels.dpaTitleMissing}
          </h2>
          <p className="text-sm text-slate-600">{labels.dpaBodyMissing}</p>

          <div className="mt-4 flex gap-2">
            <button
              onClick={onAccept}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {labels.acceptBtn} <span className="opacity-70 font-mono">v{current.current.version}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PersonaGallery: React.FC<{
  personas: Persona[];
  defaultId: string;
  lang: 'de' | 'en';
  labels: Labels;
}> = ({ personas, defaultId, lang, labels }) => (
  <section className="mb-8">
    <div className="mb-4 flex items-baseline justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 tracking-tight">{labels.personasTitle}</h2>
        <p className="text-sm text-slate-500 mt-0.5">{labels.personasSubtitle}</p>
      </div>
      <p className="text-[11px] text-slate-400 hidden sm:block">
        {lang === 'de' ? 'Lautsprecher = Stimmprobe' : 'Speaker icon = voice sample'}
      </p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {personas.map((p) => (
        <article
          key={p.id}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-slate-300"
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${p.gradient} flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm`}
            >
              {p.display_name[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-slate-900 tracking-tight truncate">
                  {p.display_name}
                </h3>
                {p.id === defaultId && (
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    default
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-snug truncate">
                {lang === 'de' ? p.vibe_de : p.vibe_en}
              </p>
            </div>
            <PersonaVoicePlayButton
              personaId={p.id}
              language={lang}
              displayName={p.display_name}
              size="md"
              variant="light"
              voiceSpeed={p.voice_speed}
            />
          </div>
        </article>
      ))}
    </div>
  </section>
);

type AreaFilter = 'all' | 'recommended' | 'tech' | 'product' | 'sales_gtm' | 'operations' | 'skilled_trades' | 'customer_service' | 'consulting' | 'marketing' | 'finance' | 'academic' | 'general';
type TypeChip = 'all' | 'behavioral' | 'coding' | 'system_design' | 'technical' | 'custom';

type TemplateFamilyGroup = {
  primary: InterviewTemplate;
  langs: string[];
  en?: InterviewTemplate;
  de?: InterviewTemplate;
};

function tplTypeLabel(type: string, lang: 'de' | 'en'): string {
  const m: Record<string, { de: string; en: string }> = {
    behavioral: { de: 'Behavioral', en: 'Behavioral' },
    coding: { de: 'Coding', en: 'Coding' },
    system_design: { de: 'System Design', en: 'System Design' },
    scenario: { de: 'Case', en: 'Case' },
    technical: { de: 'Technisch', en: 'Technical' },
  };
  return m[type]?.[lang] || type.replace(/_/g, ' ');
}

/** Recruiter template card - aligned with MockHome TemplateCard layout. */
const RecruiterTemplateCard: React.FC<{
  group: TemplateFamilyGroup;
  lang: 'de' | 'en';
  labels: Labels;
  hasActiveDpa: boolean;
  onView?: (tpl: InterviewTemplate) => void;
  onInvite?: (tpl: InterviewTemplate) => void;
  onDemo?: (payload: DemoStartPayload) => void;
  demoStarting?: string | null;
  poolGen?: Record<string, PoolGenStatus>;
  onGeneratePool?: (templateId: string, nCases: number, append?: boolean) => void;
  onCheckPoolStatus?: (templateId: string) => void;
  onEdit?: (tpl: InterviewTemplate) => void;
  onDelete?: (templateIds: string[]) => void;
  deleting?: boolean;
}> = ({ group, lang, labels, hasActiveDpa, onView, onInvite, onDemo, demoStarting, poolGen, onGeneratePool, onCheckPoolStatus, onEdit, onDelete, deleting }) => {
  const { en, de } = group;
  const [pickedLang, setPickedLang] = useState<'en' | 'de'>(() => {
    if (lang === 'de' && de) return 'de';
    if (lang === 'en' && en) return 'en';
    return (group.primary.language === 'de' ? 'de' : 'en');
  });

  useEffect(() => {
    if (lang === 'de' && de && pickedLang !== 'de') setPickedLang('de');
    else if (lang === 'en' && en && pickedLang !== 'en') setPickedLang('en');
  }, [lang, en, de]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = (pickedLang === 'en' ? en : de) || group.primary;
  const isPool = !!active.sampling_strategy;
  const isHiringOnly = !!active.available_for_recruiter && !active.available_for_practice;
  const isPracticeOnly = !!active.available_for_practice && !active.available_for_recruiter;

  // Pool generation (Phase 2 / Agent 2): only for the recruiter's own
  // private, not-yet-pool templates.
  const [genOpen, setGenOpen] = useState(false);
  const [genCases, setGenCases] = useState(5);
  const [genMode, setGenMode] = useState<'replace' | 'append'>('replace');
  const [confirmDel, setConfirmDel] = useState(false);
  const canGenPool = !!onGeneratePool && !active.is_public;
  // Number of distinct cases already in the pool (distinct task:* tags).
  const poolCaseCount = isPool
    ? new Set(
        (active.questions || [])
          .flatMap((q) => (q.tags || []))
          .filter((tg): tg is string => typeof tg === 'string' && tg.startsWith('task:')),
      ).size
    : 0;
  // On mount / when this template becomes eligible, ask the parent whether a
  // generation is already running (covers page refresh + come-back-later).
  useEffect(() => {
    if (canGenPool && onCheckPoolStatus) onCheckPoolStatus(active.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.id, canGenPool]);
  const genStatus = poolGen?.[active.id]?.status;
  const genBusy = genStatus === 'queued' || genStatus === 'running';
  const genFailed = genStatus === 'failed';

  // Rough ETA: cases are authored in batches of ~5, each batch is one LLM
  // call (~35s). Good enough to set expectations / nudge on larger pools.
  const etaLabel = (cases: number): string => {
    const sec = Math.ceil(Math.max(1, cases) / 5) * 35;
    if (sec < 60) return lang === 'de' ? 'unter 1 Min.' : 'under 1 min';
    return `~${Math.round(sec / 60)} ${lang === 'de' ? 'Min.' : 'min'}`;
  };

  const panelTags: string[] = [];
  if (active.enable_coding_panel) panelTags.push(lang === 'de' ? 'Coding' : 'Coding');
  if (active.enable_whiteboard_panel) panelTags.push(lang === 'de' ? 'Whiteboard' : 'Whiteboard');
  if (active.enable_numbers_panel) panelTags.push(lang === 'de' ? 'Zahlen' : 'Numbers');
  if (active.enable_notepad_panel) panelTags.push(lang === 'de' ? 'Notizblock' : 'Notepad');

  const stepCount = isPool && (active.questions?.length ?? 0) > 0
    ? pickSampleTaskSteps(active.questions, 0).length
    : (active.steps_per_session ?? (active.questions?.length ?? 0));

  const fixedLevelLabel = active.is_cevee_official && active.difficulty
    ? tplLevelLabel(active.difficulty, lang)
    : null;

  const langToggle = en && de ? (
    <div className="inline-flex shrink-0 bg-slate-100 rounded-full p-px text-[9px] font-semibold leading-none">
      <button
        type="button"
        onClick={() => setPickedLang('en')}
        className={'px-1.5 py-0.5 rounded-full transition ' + (pickedLang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
      >EN</button>
      <button
        type="button"
        onClick={() => setPickedLang('de')}
        className={'px-1.5 py-0.5 rounded-full transition ' + (pickedLang === 'de' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
      >DE</button>
    </div>
  ) : (
    <span className="shrink-0 text-[9px] text-slate-400 font-semibold uppercase tracking-wider">
      {active.language}
    </span>
  );

  return (
    <div className="group relative flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-slate-300">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
          {active.is_cevee_official && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-2 py-0.5 rounded-full"
              title={lang === 'de'
                ? 'Offizielles Cevee Pre-Screen Template - standardisiert und vergleichbar.'
                : 'Official Cevee Pre-Screen template - standardised and score-comparable.'}
            >
              <Sparkles className="w-3 h-3" />
              {lang === 'de' ? 'Cevee Pre-Screen' : 'Cevee Pre-Screen'}
            </span>
          )}
          {active.domain && (
            <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-pink-700 bg-pink-50 px-2 py-0.5 rounded-full">
              {areaDomainLabel(active.domain as AreaFilter, labels)}
            </span>
          )}
          {active.type && (
            <span className="inline-flex items-center text-[10px] font-medium uppercase tracking-wider text-slate-500">
              {tplTypeLabel(active.type, lang)}
            </span>
          )}
          {isHiringOnly && (
            <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">
              {lang === 'de' ? 'Einladung' : 'Invite'}
            </span>
          )}
          {isPracticeOnly && (
            <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
              {lang === 'de' ? 'Übung' : 'Practice'}
            </span>
          )}
          {!active.is_public && (
            <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
              {labels.tplPrivate}
            </span>
          )}
        </div>
        {langToggle}
      </div>

      <h3 className="mt-2.5 text-[15px] font-semibold text-slate-900 leading-snug tracking-tight">
        {cleanTemplateTitle(active.title)}
      </h3>
      {active.description && (
        <p className="mt-1 text-[13px] text-slate-500 line-clamp-2 leading-relaxed">
          {sanitizeInterviewCopy(active.description)}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-500">
        {stepCount > 0 && (
          <span
            className="inline-flex items-center gap-1 text-slate-600 font-medium"
            title={lang === 'de'
              ? 'Ein Interview mit mehreren Schritten'
              : 'One interview with several steps'}
          >
            {isPool ? (
              <Shuffle className="w-3 h-3 text-slate-400" />
            ) : (
              <ListChecks className="w-3 h-3 text-slate-400" />
            )}
            {isPool
              ? (lang === 'de' ? `~${stepCount} Schritte` : `~${stepCount} steps`)
              : (lang === 'de' ? `${stepCount} Schritte` : `${stepCount} steps`)}
          </span>
        )}
        {panelTags.map((p) => (
          <span key={p} className="inline-flex items-center gap-1 text-slate-600 font-medium">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            {p}
          </span>
        ))}
      </div>

      {isPool && !active.is_cevee_official ? (
        <div className="mt-3 px-3 py-2 rounded-xl bg-slate-50/80 border border-slate-100 flex items-center gap-2 text-[11px] text-slate-600">
          <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="leading-snug">
            {lang === 'de'
              ? <><span className="font-semibold text-slate-700">Level</span> & <span className="font-semibold text-slate-700">Dauer</span> (30 / 45 / 60 Min.) wählst du in der Einladung.</>
              : <><span className="font-semibold text-slate-700">Level</span> & <span className="font-semibold text-slate-700">duration</span> (30 / 45 / 60 min) are set in the invite.</>}
          </span>
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-slate-500 inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          {fixedLevelLabel && (
            <span className="inline-flex items-center gap-1 font-medium text-slate-600">
              Level {fixedLevelLabel}
            </span>
          )}
          {fixedLevelLabel && <span aria-hidden className="text-slate-300">·</span>}
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3 text-slate-400" />
            {active.duration_minutes} {labels.tplMin}
          </span>
        </div>
      )}

      {canGenPool && (
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
          {genBusy ? (
            (() => {
              const st = poolGen?.[active.id];
              const total = st?.n_cases;
              const done = st?.generated;
              const hasCount = typeof total === 'number' && total > 0 && typeof done === 'number';
              // The pool is authored case-by-case, so we show that honestly --
              // a single "writing distinct cases" state plus the REAL Case x/N
              // count (multi-batch pools update it live; small single-batch
              // pools just show "preparing" then snap to done).
              return (
                <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-700">
                      {lang === 'de' ? 'KI schreibt unterschiedliche Cases ...' : 'AI is writing distinct cases ...'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[11px] font-semibold text-slate-600 tabular-nums">
                      {hasCount ? `Case ${done}/${total}` : (lang === 'de' ? 'wird vorbereitet ...' : 'preparing ...')}
                    </span>
                    {hasCount && done! < total! && (
                      <span className="text-[11px] font-normal text-slate-400">
                        {lang === 'de' ? `noch ${etaLabel(total! - done!)}` : `${etaLabel(total! - done!)} left`}
                      </span>
                    )}
                  </div>
                  <div className="h-1 rounded-full bg-slate-100 overflow-hidden mb-2">
                    {hasCount ? (
                      <div
                        className="h-full bg-slate-900 transition-all duration-700 ease-out"
                        style={{ width: `${Math.max(6, Math.round((done! / total!) * 100))}%` }}
                      />
                    ) : (
                      <div className="h-full w-1/2 bg-gradient-to-r from-slate-300 via-slate-500 to-slate-300 animate-pulse" />
                    )}
                  </div>
                  <p className="text-[10.5px] text-slate-400 leading-snug">
                    {lang === 'de'
                      ? 'Läuft im Hintergrund weiter - Seite verlassen oder neu laden ist sicher, der Fortschritt bleibt.'
                      : 'Keeps running in the background - leaving or reloading the page is safe, progress is kept.'}
                  </p>
                </div>
              );
            })()
          ) : genOpen ? (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-600 leading-snug">
                {genMode === 'append'
                  ? (lang === 'de'
                      ? `Weitere Cases zum bestehenden Pool (${poolCaseCount}) hinzufügen - die neuen unterscheiden sich von den vorhandenen.`
                      : `Add more cases to the existing pool (${poolCaseCount}) - the new ones differ from what's already there.`)
                  : isPool
                    ? (lang === 'de'
                        ? 'Pool komplett neu generieren - ersetzt alle bestehenden Cases.'
                        : 'Regenerate the whole pool - replaces all existing cases.')
                    : (lang === 'de'
                        ? 'Aus diesem Template einen Pool mit mehreren Cases machen - jede:r Kandidat:in bekommt einen anderen Case (Anti-Cheat).'
                        : 'Turn this template into a pool of cases - each candidate gets a different one (anti-cheat).')}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-500">{genMode === 'append' ? (lang === 'de' ? 'Hinzufügen:' : 'Add:') : (lang === 'de' ? 'Cases:' : 'Cases:')}</span>
                {[3, 5, 10, 20].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setGenCases(n)}
                    className={
                      'text-xs font-semibold px-2.5 py-1 rounded-full border transition ' +
                      (genCases === n
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')
                    }
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setGenOpen(false); onGeneratePool?.(active.id, genCases, genMode === 'append'); }}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
                >
                  {genMode === 'append' ? <Plus className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {genMode === 'append'
                    ? (lang === 'de' ? 'Hinzufügen' : 'Add')
                    : (lang === 'de' ? 'Generieren' : 'Generate')}
                </button>
                <button
                  type="button"
                  onClick={() => setGenOpen(false)}
                  className="text-xs font-medium text-slate-400 hover:text-slate-600 px-1"
                >
                  {lang === 'de' ? 'Abbrechen' : 'Cancel'}
                </button>
              </div>
              <p className="text-[10.5px] text-slate-500 leading-snug">
                {lang === 'de'
                  ? `Dauer ca. ${etaLabel(genCases)} - läuft im Hintergrund, du kannst die Seite ruhig verlassen.`
                  : `Takes about ${etaLabel(genCases)} - runs in the background, you can safely leave the page.`}
                {genCases >= 10 && (lang === 'de' ? ' Größere Pools dauern entsprechend länger.' : ' Larger pools take proportionally longer.')}
              </p>
            </div>
          ) : isPool ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[11px] text-slate-500">
                {lang === 'de'
                  ? `Pool mit ${poolCaseCount} Case${poolCaseCount === 1 ? '' : 's'}`
                  : `Pool with ${poolCaseCount} case${poolCaseCount === 1 ? '' : 's'}`}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setGenMode('append'); setGenCases(5); setGenOpen(true); }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {lang === 'de' ? 'Mehr Cases' : 'More cases'}
                </button>
                <span aria-hidden className="text-slate-300">·</span>
                <button
                  type="button"
                  onClick={() => { setGenMode('replace'); setGenCases(5); setGenOpen(true); }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {lang === 'de' ? 'Neu generieren' : 'Regenerate'}
                </button>
              </div>
              {genFailed && (
                <span className="text-[11px] text-red-600 font-medium w-full">
                  {lang === 'de' ? 'Fehlgeschlagen - nochmal versuchen' : 'Failed - try again'}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => { setGenMode('replace'); setGenOpen(true); }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 transition"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {lang === 'de' ? 'Pool generieren' : 'Generate pool'}
              </button>
              {genFailed && (
                <span className="text-[11px] text-red-600 font-medium">
                  {lang === 'de' ? 'Fehlgeschlagen - nochmal versuchen' : 'Failed - try again'}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {onView && (
            <button
              type="button"
              onClick={() => onView(active)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 transition"
            >
              <Eye className="w-3.5 h-3.5" />
              {lang === 'de' ? 'Details' : 'Details'}
            </button>
          )}
          {onEdit && !isPool && (
            <button
              type="button"
              onClick={() => onEdit(active)}
              title={lang === 'de' ? 'Interview bearbeiten' : 'Edit interview'}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            confirmDel ? (
              <span className="inline-flex items-center gap-1 text-[11px]">
                <span className="text-slate-500">{lang === 'de' ? 'Löschen?' : 'Delete?'}</span>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDel(false);
                    onDelete([group.primary.id, group.de?.id, group.en?.id].filter(Boolean) as string[]);
                  }}
                  disabled={deleting}
                  className="font-semibold text-red-600 hover:text-red-700 px-1.5 py-0.5 rounded-full hover:bg-red-50 transition disabled:opacity-50"
                >
                  {deleting ? (lang === 'de' ? '...' : '...') : (lang === 'de' ? 'Ja' : 'Yes')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDel(false)}
                  className="font-medium text-slate-400 hover:text-slate-600 px-1"
                >
                  {lang === 'de' ? 'Nein' : 'No'}
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDel(true)}
                title={lang === 'de' ? 'Interview löschen' : 'Delete interview'}
                className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {onDemo && (
            <button
              type="button"
              onClick={() => onDemo({
                template_id: active.id,
                persona_id: active.persona_id || null,
                language: active.language || null,
              })}
              disabled={demoStarting === active.id}
              title={labels.tplDemoHint}
              className={
                'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition ' +
                (demoStarting === active.id
                  ? 'border-slate-200 text-slate-400 cursor-wait'
                  : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:text-slate-900')
              }
            >
              {demoStarting === active.id ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <PlayCircle className="w-3.5 h-3.5" />
              )}
              {labels.tplDemo}
            </button>
          )}
          {onInvite && (
            <button
              type="button"
              onClick={() => {
                if (!hasActiveDpa) return;
                onInvite(active);
              }}
              disabled={!hasActiveDpa}
              title={!hasActiveDpa ? labels.invitesGated : labels.tplInvite}
              className={
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ' +
                (hasActiveDpa
                  ? 'text-pink-600 hover:bg-pink-50'
                  : 'cursor-not-allowed text-slate-400')
              }
            >
              <Send className="w-3.5 h-3.5" />
              {labels.tplInvite}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplateSection: React.FC<{
  /** "cevee" = curated public library (read-only actions); "mine" = this
   *  company's own templates (create + AI pool generation live here). */
  mode: 'cevee' | 'mine';
  templates: InterviewTemplate[];
  hasActiveDpa: boolean;
  onCreate?: () => void;
  onView?: (tpl: InterviewTemplate) => void;
  /**
   * Click handler for the small "Einladen" button shown on each card.
   * Hub uses it to open the invite modal pre-selected to that template.
   * Gated by `hasActiveDpa` -- if no DPA, we still render the button as
   * disabled with a tooltip so recruiters understand why it's locked
   * instead of just not seeing it.
   */
  onInvite?: (tpl: InterviewTemplate) => void;
  onDemo?: (payload: DemoStartPayload) => void;
  demoStarting?: string | null;
  poolGen?: Record<string, PoolGenStatus>;
  onGeneratePool?: (templateId: string, nCases: number, append?: boolean) => void;
  onCheckPoolStatus?: (templateId: string) => void;
  onEdit?: (tpl: InterviewTemplate) => void;
  onDelete?: (templateIds: string[]) => void;
  deletingId?: string | null;
  labels: Labels;
  lang: 'de' | 'en';
}> = ({ mode, templates, hasActiveDpa, onCreate, onView, onInvite, onDemo, demoStarting, poolGen, onGeneratePool, onCheckPoolStatus, onEdit, onDelete, deletingId, labels, lang }) => {
  const [tplSearch, setTplSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState<AreaFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeChip>('all');

  const headerTitle = mode === 'mine'
    ? (lang === 'de' ? 'Eigene Interviews' : 'My interviews')
    : (lang === 'de' ? 'Cevee Interviews' : 'Cevee interviews');
  const headerSubtitle = mode === 'mine'
    ? (lang === 'de'
        ? 'Selbst erstellte Interviews und KI-generierte Pools deines Teams.'
        : 'Interviews your team created, plus AI-generated pools.')
    : (lang === 'de'
        ? 'Kuratierte, vergleichbare Pre-Screens von Cevee - sofort einsetzbar.'
        : 'Curated, comparable pre-screens by Cevee - ready to use.');

  // Dynamic area chips: only offer the areas that actually occur in THIS
  // view, so the filter row never lists a dozen empty buckets.
  const areaOptions = useMemo(() => {
    const present = new Set<string>();
    for (const t of templates) present.add((t.domain || 'general') as string);
    const all: { v: AreaFilter; label: string }[] = [
      { v: 'recommended', label: labels.areaRecommended },
      { v: 'general', label: labels.areaGeneral },
      { v: 'tech', label: labels.areaTech },
      { v: 'product', label: labels.areaProduct },
      { v: 'sales_gtm', label: labels.areaSales },
      { v: 'operations', label: labels.areaOperations },
      { v: 'skilled_trades', label: labels.areaTrades },
      { v: 'customer_service', label: labels.areaService },
      { v: 'consulting', label: labels.areaConsulting },
      { v: 'marketing', label: labels.areaMarketing },
      { v: 'finance', label: labels.areaFinance },
      { v: 'academic', label: labels.areaAcademic },
    ];
    return [
      { v: 'all' as AreaFilter, label: labels.tplAll },
      ...all.filter((o) => present.has(o.v)),
    ];
  }, [templates, labels]);
  // If the filtered-away area is no longer offered, fall back to "all".
  useEffect(() => {
    if (!areaOptions.some((o) => o.v === areaFilter)) setAreaFilter('all');
  }, [areaOptions, areaFilter]);

  // Same idea for the type row: only offer types that actually occur here.
  const typeOptions = useMemo(() => {
    const present = new Set<string>();
    for (const t of templates) if (t.type) present.add(t.type as string);
    const all: { v: TypeChip; label: string }[] = [
      { v: 'behavioral', label: lang === 'de' ? 'Verhalten' : 'Behavioral' },
      { v: 'coding', label: 'Coding' },
      { v: 'system_design', label: 'System Design' },
      { v: 'technical', label: lang === 'de' ? 'Technisch' : 'Technical' },
      { v: 'custom', label: lang === 'de' ? 'Eigen' : 'Custom' },
    ];
    return [
      { v: 'all' as TypeChip, label: labels.tplAll },
      ...all.filter((o) => present.has(o.v)),
    ];
  }, [templates, labels, lang]);
  useEffect(() => {
    if (!typeOptions.some((o) => o.v === typeFilter)) setTypeFilter('all');
  }, [typeOptions, typeFilter]);

  // Apply filters before grouping so DE/EN siblings are dropped
  // together when their family doesn't match.
  const filteredTemplates = useMemo(() => {
    const q = tplSearch.trim().toLowerCase();
    return templates.filter((t) => {
      if (areaFilter !== 'all' && (t.domain || 'general') !== areaFilter) return false;
      if (typeFilter !== 'all' && t.type !== typeFilter) return false;
      if (q) {
        const hay = `${t.title} ${t.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [templates, tplSearch, areaFilter, typeFilter]);

  return (
    <section className="mb-10">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight">{headerTitle}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{headerSubtitle}</p>
        </div>
        {mode === 'mine' && onCreate && (
          <button
            onClick={onCreate}
            disabled={!hasActiveDpa}
            title={!hasActiveDpa ? labels.tplGated : ''}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            {hasActiveDpa ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {labels.tplCreate}
          </button>
        )}
      </div>

      {templates.length > 0 && (
        <div className="mb-4 flex flex-col gap-2.5">
          <div className="relative max-w-sm">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={tplSearch}
              onChange={(e) => setTplSearch(e.target.value)}
              placeholder={labels.tplSearch}
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-7 text-sm text-slate-800 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10"
            />
            {tplSearch && (
              <button
                onClick={() => setTplSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {areaOptions.length > 2 && (
            <TplChipRow
              label={labels.tplArea}
              value={areaFilter}
              onChange={(v) => setAreaFilter(v as AreaFilter)}
              options={areaOptions}
            />
          )}
          {typeOptions.length > 2 && (
            <TplChipRow
              label={labels.tplTypeFilter}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v as TypeChip)}
              options={typeOptions}
            />
          )}
        </div>
      )}

      {templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-10 text-center">
          {mode === 'mine' ? (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-slate-500 max-w-md">
                {lang === 'de'
                  ? 'Noch keine eigenen Interviews. Erstelle ein neues (auch per KI aus einer Stellenbeschreibung) oder dupliziere ein Cevee-Interview.'
                  : 'No interviews yet. Create one (incl. AI from a job description) or duplicate a Cevee interview.'}
              </p>
              {onCreate && (
                <button
                  onClick={onCreate}
                  disabled={!hasActiveDpa}
                  title={!hasActiveDpa ? labels.tplGated : ''}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                >
                  {hasActiveDpa ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {labels.tplCreate}
                </button>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{labels.tplEmpty}</p>
          )}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-10 text-center">
          <p className="text-sm text-slate-500">{labels.tplNoMatch}</p>
        </div>
      ) : (
        (() => {
          // 1) Collapse DE/EN siblings into one card per family (like Mock).
          const groups = new Map<string, TemplateFamilyGroup>();
          for (const tpl of filteredTemplates) {
            const key = tpl.family_key || `id:${tpl.id}`;
            const g = groups.get(key);
            if (g) {
              if (!g.langs.includes(tpl.language)) g.langs.push(tpl.language);
              if (tpl.language === 'en') {
                g.en = tpl;
                g.primary = tpl;
              }
              if (tpl.language === 'de') g.de = tpl;
            } else {
              groups.set(key, {
                primary: tpl,
                langs: [tpl.language],
                en: tpl.language === 'en' ? tpl : undefined,
                de: tpl.language === 'de' ? tpl : undefined,
              });
            }
          }
          const groupList = Array.from(groups.values());

          // 2) When no Area-filter is set, render per-domain sections
          //    (mirrors the mock page so recruiters can scan by area).
          //    General leads -- behavioral applies to all roles.
          const DOMAIN_ORDER: AreaFilter[] = [
            'recommended', 'general', 'tech', 'product', 'sales_gtm',
            'operations', 'skilled_trades', 'customer_service', 'consulting',
            'marketing', 'finance',
          ];
          const sections: { domain: AreaFilter | null; items: typeof groupList }[] = [];
          if (areaFilter !== 'all') {
            sections.push({ domain: null, items: groupList });
          } else {
            const buckets: Record<string, typeof groupList> = {};
            for (const g of groupList) {
              const d = (g.primary.domain || 'general') as AreaFilter;
              (buckets[d] ||= []).push(g);
            }
            for (const d of DOMAIN_ORDER) {
              if (buckets[d]?.length) sections.push({ domain: d, items: buckets[d] });
            }
            for (const d of Object.keys(buckets)) {
              if (!DOMAIN_ORDER.includes(d as AreaFilter)) {
                sections.push({ domain: d as AreaFilter, items: buckets[d] });
              }
            }
          }

          // 2) Render per-domain sections (layout mirrors Mock library).
          return (
            <div className="space-y-7">
              {sections.map((section, idx) => (
                <div key={section.domain || `flat-${idx}`}>
                  {section.domain && areaFilter === 'all' && (
                    <div className="mb-3 flex items-baseline gap-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {areaDomainLabel(section.domain, labels)}
                      </h3>
                      <span className="text-[10px] font-medium text-slate-400">
                        {section.items.length}{' '}
                        {section.items.length === 1
                          ? (lang === 'de' ? 'Interview' : 'interview')
                          : (lang === 'de' ? 'Interviews' : 'interviews')}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 items-start">
                    {section.items.map((g) => (
                      <RecruiterTemplateCard
                        key={g.primary.family_key || g.primary.id}
                        group={g}
                        lang={lang}
                        labels={labels}
                        hasActiveDpa={hasActiveDpa}
                        onView={onView}
                        onInvite={onInvite}
                        onDemo={onDemo}
                        demoStarting={demoStarting}
                        poolGen={poolGen}
                        onGeneratePool={onGeneratePool}
                        onCheckPoolStatus={onCheckPoolStatus}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        deleting={!!deletingId && [g.primary.id, g.de?.id, g.en?.id].includes(deletingId)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </section>
  );
};

function tplLevelLabel(diff: string | null | undefined, lang: 'de' | 'en'): string {
  switch ((diff || '').toLowerCase()) {
    case 'student': return lang === 'de' ? 'Studi' : 'Student';
    case 'junior':  return 'Junior';
    case 'mid':     return lang === 'de' ? 'Mid' : 'Mid';
    case 'senior':  return 'Senior';
    case 'staff':   return 'Staff';
    default:        return diff || '';
  }
}

function areaDomainLabel(d: AreaFilter, labels: Labels): string {
  switch (d) {
    case 'recommended': return labels.areaRecommended;
    case 'tech': return labels.areaTech;
    case 'product': return labels.areaProduct;
    case 'sales_gtm': return labels.areaSales;
    case 'operations': return labels.areaOperations;
    case 'skilled_trades': return labels.areaTrades;
    case 'customer_service': return labels.areaService;
    case 'consulting': return labels.areaConsulting;
    case 'marketing': return labels.areaMarketing;
    case 'finance': return labels.areaFinance;
    case 'academic': return labels.areaAcademic;
    case 'general': return labels.areaGeneral;
    default: return String(d);
  }
}

function formatEpoch(epoch: number): string {
  const d = new Date(epoch * 1000);
  return d.toLocaleString();
}

const STATUS_PILL: Record<string, { bg: string; text: string; label_de: string; label_en: string }> = {
  invited:          { bg: 'bg-pink-50',     text: 'text-pink-700',     label_de: 'Eingeladen',     label_en: 'Invited' },
  account_linked:   { bg: 'bg-amber-50',    text: 'text-amber-700',    label_de: 'Account verlinkt', label_en: 'Account linked' },
  consent_given:    { bg: 'bg-emerald-50',  text: 'text-emerald-700',  label_de: 'Einwilligung',   label_en: 'Consent given' },
  started:          { bg: 'bg-indigo-50',   text: 'text-indigo-700',   label_de: 'Gestartet',      label_en: 'Started' },
  in_progress:      { bg: 'bg-indigo-50',   text: 'text-indigo-700',   label_de: 'Läuft',          label_en: 'In progress' },
  completed:        { bg: 'bg-emerald-100', text: 'text-emerald-800',  label_de: 'Abgeschlossen',  label_en: 'Completed' },
  expired:          { bg: 'bg-slate-100',   text: 'text-slate-600',    label_de: 'Abgelaufen',     label_en: 'Expired' },
  cancelled:        { bg: 'bg-slate-100',   text: 'text-slate-500',    label_de: 'Abgebrochen',    label_en: 'Cancelled' },
};

// ============================================================
// TplChipRow -- compact filter chip row (label + chips) used in
// the Template-Section header. Smaller than FilterBar and not tied
// to invite-status semantics.
// ============================================================

const TplChipRow: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { v: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500 w-12 flex-shrink-0">
      {label}
    </span>
    <div className="flex gap-1.5 flex-wrap">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={
              active
                ? 'text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-900 text-white shadow-sm'
                : 'text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900'
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

// ============================================================
// FilterBar -- search + status chips + optional sort dropdown.
// Reused by both InvitesSection and ResultsSection.
// ============================================================

const FilterBar: React.FC<{
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder: string;
  statusFilter: string;
  onStatusChange: (id: string) => void;
  statusOptions: { id: string; de: string; en: string }[];
  lang: 'de' | 'en';
  totalVisible: number;
  totalAll: number;
  sortValue?: string;
  sortOptions?: { id: string; de: string; en: string }[];
  onSortChange?: (id: string) => void;
}> = ({
  search,
  onSearch,
  searchPlaceholder,
  statusFilter,
  onStatusChange,
  statusOptions,
  lang,
  totalVisible,
  totalAll,
  sortValue,
  sortOptions,
  onSortChange,
}) => {
  return (
    <div className="mb-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-3 py-1.5 text-xs text-slate-700 placeholder-slate-400 rounded-lg border border-slate-200 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300"
          />
        </div>
        {sortOptions && onSortChange && (
          <div className="relative">
            <select
              value={sortValue}
              onChange={(e) => onSortChange(e.target.value)}
              className="appearance-none rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 py-1.5 text-xs text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 cursor-pointer"
            >
              {sortOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {lang === 'de' ? o.de : o.en}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}
        <span className="text-[11px] text-slate-400 ml-auto">
          {totalVisible}/{totalAll}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {statusOptions.map((o) => {
          const active = statusFilter === o.id;
          return (
            <button
              key={o.id}
              onClick={() => onStatusChange(o.id)}
              className={
                active
                  ? 'px-2.5 py-1 text-[11px] font-semibold rounded-full bg-slate-900 text-white'
                  : 'px-2.5 py-1 text-[11px] font-medium rounded-full bg-white border border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }
            >
              {lang === 'de' ? o.de : o.en}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const INVITE_STATUS_FILTERS: { id: string; de: string; en: string }[] = [
  { id: 'all', de: 'Alle', en: 'All' },
  { id: 'invited', de: 'Versendet', en: 'Sent' },
  { id: 'account_linked', de: 'Verknüpft', en: 'Linked' },
  { id: 'consent_given', de: 'Einwilligung', en: 'Consent' },
  { id: 'in_progress', de: 'Läuft', en: 'In progress' },
  { id: 'completed', de: 'Fertig', en: 'Done' },
  { id: 'expired', de: 'Abgelaufen', en: 'Expired' },
  { id: 'cancelled', de: 'Abgebrochen', en: 'Cancelled' },
];

const InvitesSection: React.FC<{
  invites: InviteSession[];
  templates: InterviewTemplate[];
  personas: Persona[];
  hasActiveDpa: boolean;
  labels: Labels;
  lang: 'de' | 'en';
  onCreate: () => void;
  onCancel: (id: string) => void | Promise<void>;
  onResend: (id: string) => void | Promise<void>;
}> = ({ invites, templates, personas, hasActiveDpa, labels, lang, onCreate, onCancel, onResend }) => {
  const templateById = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);
  const personaById = useMemo(() => Object.fromEntries(personas.map((p) => [p.id, p])), [personas]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const visibleInvites = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invites.filter((inv) => {
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (!q) return true;
      const tpl = templateById[inv.template_id];
      const hay = [
        inv.invited_email || '',
        inv.role_title || '',
        inv.role_seniority || '',
        tpl?.title || '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [invites, search, statusFilter, templateById]);

  return (
    <section className="mb-10">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight">{labels.invitesTitle}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{labels.invitesSubtitle}</p>
        </div>
        <button
          onClick={onCreate}
          disabled={!hasActiveDpa || templates.length === 0}
          title={!hasActiveDpa ? labels.invitesGated : ''}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
        >
          {hasActiveDpa ? <Mail className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          {labels.invitesCreate}
        </button>
      </div>

      {invites.length > 0 && (
        <FilterBar
          search={search}
          onSearch={setSearch}
          searchPlaceholder={lang === 'de' ? 'Suche nach Email, Rolle, Template ...' : 'Search email, role, template ...'}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={INVITE_STATUS_FILTERS}
          lang={lang}
          totalVisible={visibleInvites.length}
          totalAll={invites.length}
        />
      )}

      {invites.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-10 text-center">
          <p className="text-sm text-slate-500">{labels.invitesEmpty}</p>
        </div>
      ) : visibleInvites.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-10 text-center">
          <p className="text-sm text-slate-500">
            {lang === 'de' ? 'Keine Treffer für diese Filter.' : 'No matches for these filters.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60 border-b border-slate-100">
            <div className="col-span-3">{lang === 'de' ? 'Kandidat:in' : 'Candidate'}</div>
            <div className="col-span-3">Template</div>
            <div className="col-span-2">Persona</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Aktionen</div>
          </div>
          <ul className="divide-y divide-slate-100">
            {visibleInvites.map((inv) => {
              const tpl = templateById[inv.template_id];
              const persona = personaById[inv.persona_id];
              const status = STATUS_PILL[inv.status] || STATUS_PILL.invited;
              const cancellable = ['invited', 'account_linked', 'consent_given'].includes(inv.status);
              const resendable = ['invited', 'account_linked'].includes(inv.status);
              return (
                <li key={inv.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm">
                  <div className="col-span-3 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{inv.invited_email || '-'}</p>
                    <p className="text-[11px] text-slate-400">
                      {inv.invite_sent_at
                        ? `${labels.sent}: ${formatEpoch(inv.invite_sent_at)}`
                        : (lang === 'de' ? 'Email wird versendet ...' : 'Sending email ...')}
                    </p>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <p className="text-slate-700 truncate">
                      {inv.role_title}
                      {inv.role_seniority && (
                        <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400">
                          {inv.role_seniority}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-400 truncate">
                      {tpl?.title ? cleanTemplateTitle(tpl.title) : inv.template_id.slice(0, 8)}
                    </p>
                    {inv.invite_expires_at && (
                      <p className="text-[11px] text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {labels.expiresAt}: {formatEpoch(inv.invite_expires_at)}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 min-w-0">
                    {persona && (
                      <div className="flex items-center gap-1.5">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${persona.gradient} flex items-center justify-center text-white text-[10px] font-bold`}>
                          {persona.display_name[0]}
                        </div>
                        <span className="text-xs text-slate-600">{persona.display_name}</span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <span className={`${status.bg} ${status.text} text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md`}>
                      {lang === 'de' ? status.label_de : status.label_en}
                    </span>
                  </div>
                  <div className="col-span-2 flex justify-end gap-1.5 text-[11px]">
                    {resendable && (
                      <button
                        onClick={() => onResend(inv.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600"
                        title={labels.resendBtn}
                      >
                        <RotateCcw className="w-3 h-3" />
                        {labels.resendBtn}
                      </button>
                    )}
                    {cancellable && (
                      <button
                        onClick={() => onCancel(inv.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-red-50 text-red-600"
                        title={labels.cancelBtn}
                      >
                        <XIcon className="w-3 h-3" />
                        {labels.cancelBtn}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

// ============================================================
// ResultsSection -- completed interviews + score badges
// ============================================================

const RESULT_REVIEW_FILTERS: { id: string; de: string; en: string }[] = [
  { id: 'all', de: 'Alle', en: 'All' },
  { id: 'reviewed', de: 'Geprüft', en: 'Reviewed' },
  { id: 'pending', de: 'Offen', en: 'Pending' },
];

type ResultSort = 'newest' | 'score_desc' | 'score_asc';

const ResultsSection: React.FC<{
  results: ResultSummary[];
  demos: DemoHistoryItem[];
  lang: 'de' | 'en';
}> = ({ results, demos, lang }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [reviewFilter, setReviewFilter] = useState<string>('all');
  const [sort, setSort] = useState<ResultSort>('newest');
  const [showDemos, setShowDemos] = useState(false);
  const RESULTS_PAGE = 8;
  const [resultLimit, setResultLimit] = useState(RESULTS_PAGE);

  // Reset the visible window whenever the filtered set changes so search /
  // filter / sort always start from the top of the (new) list.
  useEffect(() => {
    setResultLimit(RESULTS_PAGE);
  }, [search, reviewFilter, sort]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = results.filter((r) => {
      if (reviewFilter === 'reviewed' && !r.is_reviewed) return false;
      if (reviewFilter === 'pending' && r.is_reviewed) return false;
      if (!q) return true;
      const hay = [
        r.candidate_label || '',
        r.invited_email || '',
        r.role_title || '',
        r.role_seniority || '',
        r.template_title || '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
    const scoreOf = (r: ResultSummary) => r.final_score ?? r.overall_score ?? -1;
    if (sort === 'score_desc') return [...filtered].sort((a, b) => scoreOf(b) - scoreOf(a));
    if (sort === 'score_asc') return [...filtered].sort((a, b) => scoreOf(a) - scoreOf(b));
    return [...filtered].sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
  }, [results, search, reviewFilter, sort]);

  const sectionHeader = (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
          {lang === 'de' ? 'Ergebnisse & Reviews' : 'Results & reviews'}
        </h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {lang === 'de'
            ? 'Abgeschlossene Interviews. KI-Score, Empfehlung, und Human-in-the-Loop Review.'
            : 'Completed interviews. AI score, recommendation, and human-in-the-loop review.'}
        </p>
      </div>
    </div>
  );

  const fmtDuration = (s: number | null) =>
    s != null ? `${Math.floor(s / 60)} min` : 'n/a';

  // Recruiter demos run with the candidate experience but live under the
  // recruiter's own account. We surface them here (above the real
  // results) with a clear "Demo" badge so they're never confused with
  // actual candidate interviews. Click -> the mock review page (the demo
  // session is is_mock=true and owned by the recruiter).
  const demosBlock = demos.length > 0 && (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setShowDemos((o) => !o)}
        className="mb-2 flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-slate-50"
      >
        <PlayCircle className="w-4 h-4 text-slate-400" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {lang === 'de' ? 'Deine Demo-Durchläufe' : 'Your demo runs'}
        </h3>
        <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-bold text-slate-500">{demos.length}</span>
        <span className="ml-auto text-[11px] font-medium text-slate-400">
          {showDemos
            ? (lang === 'de' ? 'Ausblenden' : 'Hide')
            : (lang === 'de' ? 'Anzeigen' : 'Show')}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${showDemos ? 'rotate-180' : ''}`} />
      </button>
      {showDemos && (
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {demos.map((d) => (
          <li
            key={d.session_id}
            className="py-3 px-4 grid grid-cols-[1fr_auto] gap-3 items-center cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => navigate(`/mock/sessions/${d.session_id}`)}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full">
                  Demo
                </span>
                <span className="truncate">{d.template_title || (lang === 'de' ? 'Demo-Interview' : 'Demo interview')}</span>
              </p>
              <p className="text-xs text-slate-500 truncate">
                {tplLevelLabel(d.difficulty, lang)} · {d.persona_id} · {fmtDuration(d.duration_seconds)}
              </p>
            </div>
            <ScoreBadge
              score={d.overall_score}
              recommendation={d.recommendation}
              isReviewed={false}
              aiConfidence={d.ai_confidence}
              status={d.status}
              lang={lang}
            />
          </li>
        ))}
      </ul>
      )}
    </div>
  );

  if (results.length === 0) {
    return (
      <section className="mb-10">
        {sectionHeader}
        {demosBlock}
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-10 text-center">
          <p className="text-sm text-slate-500">
            {lang === 'de'
              ? 'Noch keine abgeschlossenen Interviews. Versende eine Einladung, um zu starten.'
              : 'No completed interviews yet. Send an invite to get started.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-10">
      {sectionHeader}
      {demosBlock}

      <FilterBar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={lang === 'de' ? 'Suche nach Kandidat:in, Rolle ...' : 'Search candidate, role ...'}
        statusFilter={reviewFilter}
        onStatusChange={setReviewFilter}
        statusOptions={RESULT_REVIEW_FILTERS}
        lang={lang}
        totalVisible={visible.length}
        totalAll={results.length}
        sortValue={sort}
        sortOptions={[
          { id: 'newest', de: 'Neueste', en: 'Newest' },
          { id: 'score_desc', de: 'Score (hoch)', en: 'Score (high)' },
          { id: 'score_asc', de: 'Score (niedrig)', en: 'Score (low)' },
        ]}
        onSortChange={(v) => setSort(v as ResultSort)}
      />

      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-8 text-center mt-3">
          <p className="text-sm text-slate-500">
            {lang === 'de' ? 'Keine Treffer für diese Filter.' : 'No matches for these filters.'}
          </p>
        </div>
      ) : (
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {visible.slice(0, resultLimit).map((r) => (
          <li
            key={r.session_id}
            className="py-3 px-4 grid grid-cols-[1fr_auto] gap-3 items-center cursor-pointer hover:bg-slate-50/60 transition-colors"
            onClick={() => navigate(`/interviews/${r.session_id}`)}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {r.candidate_label}
                <span className="ml-2 text-xs font-medium text-slate-500">
                  &middot; {r.role_title}
                  {r.role_seniority ? ` (${r.role_seniority})` : ''}
                </span>
                {r.safety_flag_count > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                    {r.safety_flag_count} {lang === 'de' ? 'Sicherheits-Flag' : 'safety flag'}{r.safety_flag_count === 1 ? '' : 's'}
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {r.template_title || 'n/a'} · {r.persona_id} · {fmtDuration(r.duration_seconds)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ScoreBadge
                score={r.final_score ?? r.overall_score}
                recommendation={r.final_recommendation ?? r.recommendation}
                isReviewed={r.is_reviewed}
                aiConfidence={r.ai_confidence}
                status={r.status}
                lang={lang}
              />
            </div>
          </li>
        ))}
      </ul>
      )}

      {visible.length > resultLimit && (
        <button
          type="button"
          onClick={() => setResultLimit((n) => n + RESULTS_PAGE)}
          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
        >
          {lang === 'de'
            ? `Mehr anzeigen (${visible.length - resultLimit})`
            : `Show more (${visible.length - resultLimit})`}
        </button>
      )}
    </section>
  );
};

const ScoreBadge: React.FC<{
  score: number | null;
  recommendation: string | null;
  isReviewed: boolean;
  aiConfidence?: number | null;
  status?: string;
  lang: 'de' | 'en';
}> = ({ score, recommendation, isReviewed, aiConfidence, status, lang }) => {
  // Sentinel from backend `_persist_no_data`: ai_confidence=0 + score=null means
  // the session had no transcript (candidate dropped before speaking). Show
  // "Abgebrochen" instead of misleading "In Bearbeitung" / "neutral".
  const isAbandoned =
    score == null && aiConfidence === 0;
  if (isAbandoned) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
        {lang === 'de' ? 'Abgebrochen \u00b7 kein Transkript' : 'Abandoned \u00b7 no transcript'}
      </span>
    );
  }
  // For sessions that never completed at all, status is invited/expired etc.
  // No badge until the candidate actually started.
  const isCompleted = status === 'completed';
  if (score == null && !recommendation) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
        {isCompleted
          ? (lang === 'de' ? 'In Bearbeitung' : 'Generating...')
          : (lang === 'de' ? '\u2013' : '\u2013')}
      </span>
    );
  }
  const recCls =
    recommendation === 'strong_hire' ? 'bg-emerald-100 text-emerald-700'
    : recommendation === 'hire' ? 'bg-green-100 text-green-700'
    : recommendation === 'neutral' ? 'bg-amber-100 text-amber-700'
    : recommendation === 'no_hire' ? 'bg-orange-100 text-orange-700'
    : recommendation === 'strong_no_hire' ? 'bg-red-100 text-red-700'
    : 'bg-slate-100 text-slate-600';
  return (
    <div className="flex items-center gap-2">
      {recommendation && (
        <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${recCls}`}>
          {recommendation.replace(/_/g, ' ')}
        </span>
      )}
      {/* Score hidden from UI (data still tracked in backend) */}
      {isReviewed && (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
          {lang === 'de' ? 'Geprüft' : 'Reviewed'}
        </span>
      )}
    </div>
  );
};

export default InterviewHub;
